import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, Injector, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { AuthState, changePwBody, LoginRequest, LoginResponse } from '../types/auth';
import { Observable, of, tap, catchError, throwError, map } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TokenData } from '../types/tokenData';
import { jwtDecode } from 'jwt-decode';
import { Role, User } from '../types/user';
import { ExternalTokenData } from '../types/external-token';
import { EesztAuthService } from './eeszt-auth.service';

@Injectable({
  providedIn: 'root'
})
export class Auth {

  private readonly apiUrl = environment.apiUrl;

  private http = inject(HttpClient);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  private injector = inject(Injector);

  tokenData = signal<TokenData | null>(null);
  currentUser = signal<User | null>(null);
  userLoading = signal<boolean>(false);
  externalTokenData = signal<ExternalTokenData | null>(null);

  isLoggedIn = computed(() => {
    const token = this.tokenData();
    return token !== null && !this.isTokenExpired();
  });

  isTokenExpired = computed(() => {
    const token = this.tokenData();
    if (!token) return true;
    const now = Math.floor(Date.now() / 1000);
    return token.exp < now;
  });

  authState = computed((): AuthState => ({
    isLoggedIn: this.isLoggedIn(),
    user: this.currentUser(),
    token: this.tokenData(),
    isExpired: this.isTokenExpired()
  }));

  // Permission helper computed signals
  isAdmin = computed(() => {
    return this.hasPermission('ADMIN');
  });

  isSenderDoctor = computed(() => {
    return this.hasPermission('REF_PHYS');
  });

  isAssistant = computed(() => {
    return this.hasPermission('ASSISTANT');
  });

  isSender = computed(() => {
    return this.hasPermission('REF_PHYS') || this.hasPermission('ASSISTANT');
  });

  isReportingDoctorOrAssistant = computed(() => {
    return this.hasPermission('EXAM_PHYS');
  });

  isReportingDoctor = computed(() => {
    return this.hasPermission('DOCTOR');
  });

  isQualityControl = computed(() => {
    return this.hasPermission('START_QC') || this.hasPermission('EDIT_QC') || this.hasPermission('DELETE_QC');
  });

  constructor() {
  }

  // Permission helper method
  hasPermission(permissionCode: string): boolean {
    const user = this.currentUser();
    if (!user || !user.role || !user.role.permissions) {
      return false;
    }
    return user.role.permissions.some(permission => permission.code === permissionCode);
  }

  login(username: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/rest/auth/login`, { username, password, deviceInfo: "" } as LoginRequest).pipe(
      tap((response) => {
        this.setAuthData(response.token, true); // true = navigate after loading user
        this.toastr.success('Sikeres bejelentkezés');
      }),
      catchError((error) => {
        // this.toastr.error('Hiba történt a bejelentkezés során!');
        return throwError(() => error);
      })
    );
  }

  decodeExternalToken(token: string): ExternalTokenData | null {
    try {
      return jwtDecode(token) as ExternalTokenData;
    } catch (error) {
      console.error('Failed to decode external token:', error);
      return null;
    }
  }

  loginWithExternalToken(token: string): Observable<LoginResponse> {
    const tokenData = this.decodeExternalToken(token);
    if (!tokenData?.user) {
      return throwError(() => new Error('Invalid external token'));
    }

    // Store external token data for later use by other components
    this.externalTokenData.set(tokenData);

    // Perform login with embedded credentials
    return this.login(tokenData.user.username, tokenData.user.password);
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/rest/auth/logout`, {}).pipe(
      tap(() => {
        this.clearAuthData();
        this.toastr.info('Kijelentkezve');
      }),
      catchError(() => {
        // Even if server logout fails, clear local data
        this.clearAuthData();
        this.toastr.info('Kijelentkezve');
        return of(void 0);
      })
    );
  }

  logoutLocally(): void {
    this.clearAuthData();
    this.toastr.info('Kijelentkezve');
    this.router.navigate(['/']);
  }

  getToken(): string | null {
    const token = localStorage.getItem('token');
    return token;
  }

  requestPasswordReset(identifier: string): Observable<void> {
    return this.http
      .post(this.apiUrl + '/rest/user/forgot/' + identifier, {}, { responseType: 'text' as const })
      .pipe(map(() => void 0));
  }

  changePassword(body: changePwBody, isActivationCode: boolean = false): Observable<void> {
    return this.http
      .put(this.apiUrl + '/rest/user/change-password' + (isActivationCode ? '/true' : '/false'), body, { responseType: 'text' as const })
      .pipe(map(() => void 0));
  }

  getUserByToken(): Observable<User> {
    return this.http.get<User>(this.apiUrl + '/rest/user/jwt');
  }

  private loadCurrentUser(navigate: boolean = false): void {
    this.userLoading.set(true);

    this.getUserByToken().pipe(
      tap((user) => {
        this.currentUser.set(user);
        this.userLoading.set(false);

        // Navigate based on role only if this is from login flow
        if (navigate && user?.role) {
          this.navigateByRole(user.role);
        }
      }),
      catchError((error) => {
        console.error('Failed to load user:', error);
        this.currentUser.set(null);
        this.userLoading.set(false);
        return of(null);
      })
    ).subscribe();
  }

  initializeAuth(navigate: boolean = false): void {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const tokenData = jwtDecode(token) as TokenData;
        const now = Math.floor(Date.now() / 1000);

        if (tokenData.exp < now) {
          this.clearAuthData();
          return;
        }

        this.tokenData.set(tokenData);
        this.loadCurrentUser(navigate);
      } catch (error) {
        console.error('Invalid token:', error);
        this.clearAuthData();
      }
    }
  }

  private setAuthData(token: string, navigate: boolean = false): void {
    localStorage.setItem('token', token);
    const tokenData = jwtDecode(token) as TokenData;
    this.tokenData.set(tokenData);
    this.loadCurrentUser(navigate);
  }

  private clearAuthData(): void {
    localStorage.removeItem('token');
    this.tokenData.set(null);
    this.currentUser.set(null);
    // Lazy inject to avoid circular dependency
    this.injector.get(EesztAuthService).clearToken();
  }

  navigateByRole(role: Role): void {
    // Admin routes
    // if (role.permissions.some(permission => permission.code === 'CREATE_SUBMITTER' || permission.code === 'CREATE_USER' || permission.code === 'EDIT_LAB_WHO' || permission.code === 'CREATE_EXAMINATION')) {
    if (this.isAdmin()) {
      this.router.navigate(['/admin']);
      return;
    }

    // Sender routes
    // if (role.permissions.some(permission => permission.code === 'CREATE_ORDER_AND_REQUISITION')) {
    if (this.isSender()) {
      this.router.navigate(['/reports/request/new'], { queryParamsHandling: 'merge' })
      return;
    }

    // Default route (validator)
    this.router.navigate(['/reports']);
  }
}
